import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiDownload, FiUpload, FiTrendingUp, FiImage } from 'react-icons/fi';

interface TrendingNews {
  id: number;
  title?: string;
  description?: string;
  category: string;
  image: string;
 
  created_at: string;
}

const TrendingNews: React.FC = () => {
  const [news, setNews] = useState<TrendingNews[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Add News Entry state
  const [addForm, setAddForm] = useState({
    title: '',
    description: '',
    category: '',
   
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);

  const handleAddFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setAddForm(f => ({ ...f, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Add/Update News Entry handler
  const handleSaveNews = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    setAddSuccess('');
    
    // Custom validation: At least title, description, or image must be provided
    const hasTitle = addForm.title.trim();
    const hasDescription = addForm.description.trim();
    const hasImage = selectedImage || imagePreview;
    
    if (!hasTitle && !hasDescription && !hasImage) {
      setAddError('Please provide at least a title, description, or image.');
      return;
    }
    
    setAddLoading(true);
    try {
      const url = 'https://mandilive.in/api/endpoints/trending_news.php';
      
      if (editingId) {
        // For updates, use JSON (no file upload on updates for now)
        const payload = {
          id: editingId,
          ...addForm,
          image: imagePreview || '', // Keep existing image if no new one
        };
        
        const res = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        
        if (res.ok) {
          setAddSuccess('News entry updated.');
          setAddError('');
          setEditingId(null);
          // Reset form
          setAddForm({
            title: '',
            description: '',
            category: '',
          });
          setSelectedImage(null);
          setImagePreview('');
          // Refresh news
          fetchNews();
        } else {
          let err;
          try {
            err = await res.json();
          } catch (e) {
            err = { message: res.statusText };
          }
          console.error('API error:', res.status, err);
          setAddError(err.message || 'Failed to update news entry');
        }
      } else {
        // For new entries, use FormData to handle file upload
        const formData = new FormData();
        formData.append('title', addForm.title);
        formData.append('description', addForm.description);
        formData.append('category', addForm.category);
        
        if (selectedImage) {
          formData.append('image', selectedImage);
        }
        
        const res = await fetch(url, {
          method: 'POST',
          body: formData, // No Content-Type header for FormData
        });
        
        if (res.ok) {
          setAddSuccess('News entry added.');
          setAddError('');
          // Reset form
          setAddForm({
            title: '',
            description: '',
            category: '',
          });
          setSelectedImage(null);
          setImagePreview('');
          // Refresh news
          fetchNews();
        } else {
          let err;
          try {
            err = await res.json();
          } catch (e) {
            err = { message: res.statusText };
          }
          console.error('API error:', res.status, err);
          setAddError(err.message || 'Failed to save news entry');
        }
      }
    } catch (err: any) {
      setAddError(err?.message || 'Failed to save news entry');
    }
    setAddLoading(false);
  };

  // Fetch news data from API
  const fetchNews = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://mandilive.in/api/endpoints/trending_news.php');
      const data = await res.json();
      setNews(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError('Failed to fetch trending news: ' + (err?.message || err));
      setNews([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const handleEdit = (item: TrendingNews) => {
    setEditingId(item.id);
    setAddForm({
      title: item.title || '',
      description: item.description || '',
      category: item.category,
     
    });
    if (item.image) {
      setImagePreview(item.image);
    }
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setAddForm({
      title: '',
      description: '',
      category: '',
    
    });
    setSelectedImage(null);
    setImagePreview('');
    setAddError('');
    setAddSuccess('');
  };

  // Delete news entry handler
  const handleDelete = async (id: number, title?: string) => {
    const displayTitle = title || 'this image-only entry';
    if (!window.confirm(`Are you sure you want to delete "${displayTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch('https://mandilive.in/api/endpoints/trending_news.php', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      });

      if (response.ok) {
        const result = await response.json();
        // Remove from local state immediately for better UX
        setNews(prevNews => prevNews.filter(item => item.id !== id));
        setAddSuccess(result.message || `News article "${title}" deleted successfully.`);
        setAddError('');
      } else {
        let errorMessage = 'Failed to delete news article';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        setAddError(errorMessage);
        setAddSuccess('');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setAddError('Network error: Failed to delete news article');
      setAddSuccess('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Trending News Management</h1>
       
      </div>

      {/* Add/Edit News Entry Form */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <FiPlus className="mr-2" />
          {editingId ? 'Edit News Entry' : 'Add New News Entry'}
        </h2>
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> You can now create image-only trending news entries. At least one of the following must be provided: title, description, or image.
          </p>
        </div>
        <form onSubmit={handleSaveNews} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title (Optional)</label>
              <input
                type="text"
                name="title"
                value={addForm.title}
                onChange={handleAddFormChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="News title"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input
                type="text"
                name="category"
                value={addForm.category}
                onChange={handleAddFormChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Agriculture, Market, Technology"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
            <textarea
              name="description"
              value={addForm.description}
              onChange={handleAddFormChange}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="News description"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-md border"
                  />
                </div>
              )}
            </div>
        
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={addLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {addLoading ? 'Saving...' : (
                <>
                  <FiPlus className="mr-2" />
                  {editingId ? 'Update Entry' : 'Save Entry'}
                </>
              )}
            </button>
            
            {editingId && (
              <button
                type="button"
                onClick={cancelEdit}
                className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
        
        {addError && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {addError}
          </div>
        )}
        
        {addSuccess && (
          <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {addSuccess}
          </div>
        )}
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading trending news...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* News Cards Grid */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <FiTrendingUp className="mr-2 text-red-600" />
            Trending News ({news.length})
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage and organize trending news articles
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((item) => (
              <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                {item.image && (
                  <div className="h-48 bg-gray-200 relative">
                    <img
                      src={`https://mandilive.in/${item.image}`}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                    <div className="absolute top-2 right-2">
                      <FiImage className="text-white bg-black bg-opacity-50 p-1 rounded" size={24} />
                    </div>
                  </div>
                )}
                
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    {item.title ? (
                      <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                        {item.title}
                      </h3>
                    ) : (
                      <h3 className="text-lg font-semibold text-gray-500 italic line-clamp-2">
                        Image only (No title)
                      </h3>
                    )}
                    {item.category && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full whitespace-nowrap">
                        {item.category}
                      </span>
                    )}
                  </div>
                  
                  {item.description ? (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                      {item.description}
                    </p>
                  ) : (
                    <p className="text-gray-400 text-sm mb-3 italic">
                      No description provided
                    </p>
                  )}
                  
                 
                  
                  <div className="flex justify-between items-center">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-blue-600 hover:text-blue-900 flex items-center text-sm"
                      >
                        <FiEdit className="mr-1" /> Edit
                      </button>
                    </div>
                    <button
                      onClick={() => handleDelete(item.id, item.title)}
                      className="text-red-600 hover:text-red-900 flex items-center text-sm px-2 py-1 rounded hover:bg-red-50 transition-colors"
                      title={`Delete "${item.title}"`}
                    >
                      <FiTrash2 className="mr-1" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {news.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              <FiTrendingUp className="mx-auto mb-4" size={48} />
              <p>No trending news found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrendingNews;
